// ============================================================
// FILE: src/pages/Countries.jsx  →  COUNTRIES PAGE
// The rotating globe + 6 clickable region cards.
// Region/country names are in src/data/site.js (WORLD REGIONS).
// ============================================================

import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Anchor, MapPin } from "lucide-react";
import PageHero from "../components/PageHero";
import DotGlobe from "../components/DotGlobe";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { REGIONS } from "../data/site";

export default function Countries() {
  useSEO("Countries & Trade Corridors — Evolve Trade & Logistics", "Active trade corridors across the Middle East, Europe, Africa, Asia, North America and Australia — 40+ countries served.");
  const [open, setOpen] = useState("Middle East");

  return (
    <>
      <PageHero
        label="Global Reach"
        title="Forty countries,"
        accent="six corridors"
        description="Active trade lanes across the Middle East, Europe, Africa, Asia, North America and Australia — with port-level expertise in each."
        crumbs={[{ label: "Countries" }]}
      />

      <section className="pb-20" data-testid="countries-globe-section">
        <div className="container-x">
          <Reveal>
            <div className="relative mx-auto h-[420px] max-w-3xl sm:h-[520px]">
              <DotGlobe className="h-full w-full" density={900} speed={0.0012} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="regions-section">
        <div className="container-x">
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REGIONS.map((r) => {
              const isOpen = open === r.name;
              return (
                <StaggerItem key={r.name}>
                  <div
                    className={`card-surface h-full cursor-pointer !p-6 ${isOpen ? "!border-teal-500/60" : ""}`}
                    onClick={() => setOpen(isOpen ? "" : r.name)}
                    data-testid={`region-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl font-semibold">{r.name}</h3>
                      <span className="chip !text-[10px]">{r.countries.length} markets</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.blurb}</p>
                    <p className="mt-4 flex items-center gap-2 text-xs font-medium text-teal-600 dark:text-teal-400">
                      <Anchor className="h-3.5 w-3.5" /> {r.ports}
                    </p>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-900/10 pt-5 dark:border-white/10">
                            {r.countries.map((c) => (
                              <span key={c} className="chip" data-testid={`country-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                                <MapPin className="h-3 w-3 text-teal-500" /> {c}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          <Reveal className="mt-14 text-center">
            <p className="text-sm text-muted-foreground">Shipping to a market not listed? We open new corridors every quarter.</p>
            <Link to="/contact" className="btn-primary mt-6" data-testid="countries-cta">Ask About Your Market <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
